// Browser-only loader for the self-hosted world basemap. Heavy renderer code is
// kept behind this function so it stays outside the initial application bundle.

export const MAP_DATA_URL =
  "https://map.mylocation.app/releases/world-20260902.pmtiles";

const MAP_ASSET_ROOT = "/map-assets/basemaps-5.7.2";
// @mapbox/mapbox-gl-rtl-text 0.4.0 (BSD-2-Clause), copied unchanged into static/.
const RTL_TEXT_PLUGIN = "/map-assets/mapbox-gl-rtl-text-0.4.0.js";
/** @type {Promise<any> | undefined} */
let rendererPromise;

export function loadMapRenderer() {
  if (!rendererPromise) {
    // Establish the tile connection while the renderer downloads, only after
    // a map is requested. Marketing pages need no speculative map connection.
    if (typeof document !== "undefined" && !document.querySelector('link[data-map-preconnect]')) {
      const hint = document.createElement("link");
      hint.rel = "preconnect";
      hint.href = new URL(MAP_DATA_URL).origin;
      hint.crossOrigin = "anonymous";
      hint.dataset.mapPreconnect = "";
      document.head.append(hint);
    }
    rendererPromise = Promise.all([
      import("maplibre-gl"),
      import("maplibre-gl/dist/maplibre-gl.css"),
      import("pmtiles"),
      import("@protomaps/basemaps"),
      import("maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url"),
    ]).then(([maplibregl, _css, pmtiles, basemaps, worker]) => {
      // v6 is ESM-only. Let Vite bundle the worker and its dependencies as a
      // same-origin asset instead of resolving it relative to an app chunk.
      maplibregl.setWorkerUrl(worker.default);
      const protocol = new pmtiles.Protocol({ metadata: true });

      maplibregl.addProtocol("pmtiles", protocol.tile);
      protocol.add(new pmtiles.PMTiles(MAP_DATA_URL));

      // Arabic and Hebrew labels need this add-on to join their letters and run
      // right to left. It is served from our own site and, being "lazy", is only
      // downloaded when such a label first appears on the map.
      try {
        if (maplibregl.getRTLTextPluginStatus?.() === "unavailable") {
          const pending = maplibregl.setRTLTextPlugin(
            new URL(RTL_TEXT_PLUGIN, document.baseURI).href,
            true,
          );
          pending?.catch?.((error) =>
            console.warn("Right-to-left map labels are unavailable", error),
          );
        }
      } catch (error) {
        console.warn("Right-to-left map labels are unavailable", error);
      }

      // A PMTiles archive can retain a rejected header/directory promise.
      // Explicit retries need a fresh archive cache, not just a new map view.
      const resetTileCache = () => protocol.add(new pmtiles.PMTiles(MAP_DATA_URL));
      return { maplibregl, basemaps, resetTileCache };
    }).catch((error) => {
      rendererPromise = undefined;
      throw error;
    });
  }

  return rendererPromise;
}

/** @param {any} basemaps
 * @returns {import('maplibre-gl').StyleSpecification}
 */
export function createBasemapStyle(basemaps, dark = false) {
  const language =
    typeof document === "undefined"
      ? "en"
      : (document.documentElement.lang || "en").toLowerCase().startsWith("zh")
        ? "zh-Hans"
        : (document.documentElement.lang || "en").split("-")[0];
  const assetRoot = new URL(
    MAP_ASSET_ROOT,
    typeof document === "undefined" ? "https://localhost" : document.baseURI,
  ).href;

  return {
    version: 8,
    glyphs: `${assetRoot}/fonts/{fontstack}/{range}.pbf`,
    sprite: `${assetRoot}/sprites/v4/light`,
    sources: {
      basemap: {
        type: "vector",
        url: `pmtiles://${MAP_DATA_URL}`,
        attribution:
          "<a href='https://github.com/protomaps/basemaps'>Protomaps</a> · " +
          "© <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap contributors</a>",
      },
    },
    layers: basemaps.layers(
      "basemap",
      basemaps.namedFlavor(dark ? "dark" : "light"),
      {
        lang: language || "en",
      },
    ),
  };
}
